interface Props {
  /** ISO day numbers (1=Mon ... 7=Sun). Empty = available every day. */
  days: number[];
  setDays: (next: number[]) => void;
  /** "HH:mm" strings (or "" to inherit category default). */
  fromTime: string;
  toTime: string;
  setFromTime: (v: string) => void;
  setToTime: (v: string) => void;
}

const DAY_OPTIONS: { id: number; short: string }[] = [
  { id: 1, short: "Mon" },
  { id: 2, short: "Tue" },
  { id: 3, short: "Wed" },
  { id: 4, short: "Thu" },
  { id: 5, short: "Fri" },
  { id: 6, short: "Sat" },
  { id: 7, short: "Sun" },
];

const PRESETS: { label: string; days: number[] }[] = [
  { label: "Every day", days: [] },
  { label: "Weekdays", days: [1, 2, 3, 4, 5] },
  { label: "Weekends", days: [6, 7] },
];

const AvailabilityEditor: React.FC<Props> = ({
  days, setDays, fromTime, toTime, setFromTime, setToTime,
}) => {
  const toggle = (d: number) => {
    setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b));
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs text-ink-500 font-semibold mb-1.5">Days</p>
        <div className="flex gap-1.5 mb-2 flex-wrap">
          {PRESETS.map((p) => {
            const active = JSON.stringify([...days].sort()) === JSON.stringify([...p.days].sort());
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => setDays(p.days)}
                className={`px-3 h-[28px] rounded-full text-[11px] font-bold uppercase tracking-wider transition ${
                  active ? "bg-ink-1000 text-white" : "bg-ink-100 text-ink-700"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {DAY_OPTIONS.map((d) => {
            const active = days.length === 0 || days.includes(d.id);
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => {
                  // First explicit pick: collapse "every day" → only the tapped day
                  if (days.length === 0) setDays([d.id]);
                  else toggle(d.id);
                }}
                className={`h-[40px] rounded-xl text-xs font-bold transition border-2 ${
                  active
                    ? "bg-ink-1000 text-white border-ink-1000"
                    : "bg-ink-50 text-ink-500 border-transparent"
                }`}
              >
                {d.short}
              </button>
            );
          })}
        </div>
        {days.length === 0 && (
          <p className="text-[11px] text-ink-400 mt-1.5">Currently available every day. Tap a day to limit to specific days.</p>
        )}
      </div>

      <div>
        <p className="text-xs text-ink-500 font-semibold mb-1.5">Time window</p>
        <div className="flex items-center gap-2">
          <input
            type="time"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
            aria-label="Available from time"
            className="flex-1 h-[44px] px-3 rounded-xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-sm text-ink-1000"
          />
          <span className="text-ink-400 text-xs">to</span>
          <input
            type="time"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
            aria-label="Available until time"
            className="flex-1 h-[44px] px-3 rounded-xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-sm text-ink-1000"
          />
          {(fromTime || toTime) && (
            <button
              type="button"
              onClick={() => { setFromTime(""); setToTime(""); }}
              className="px-3 h-[44px] rounded-xl bg-ink-50 hover:bg-ink-100 text-xs font-semibold text-ink-700"
            >
              Clear
            </button>
          )}
        </div>
        <p className="text-[11px] text-ink-400 mt-1.5">Leave blank to inherit the category's default hours.</p>
      </div>
    </div>
  );
};

export default AvailabilityEditor;
